import React, { useState, useEffect } from 'react';
import { FileText, Plus, Download, Trash2, Edit, Settings } from 'lucide-react';
import Card, { CardHeader, CardContent } from '../components/ui/Card';
import Button from '../components/ui/Button';
import ServiceAgreementModal from '../components/modals/ServiceAgreementModalSimple';
import TemplateEditorModal from '../components/modals/TemplateEditorModal';
import { useAppStore } from '../store';
import { generateServiceAgreementPDF } from '../utils/serviceAgreementPdfGenerator';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../hooks/useToast';

const ServiceAgreements: React.FC = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isTemplateEditorOpen, setIsTemplateEditorOpen] = useState(false);
  const [editingAgreement, setEditingAgreement] = useState(null);

  const { clients = [], serviceAgreements = [], fetchServiceAgreements, addServiceAgreement, deleteServiceAgreement } = useAppStore();
  const { user, userRole } = useAuth();
  const { toast, ToastComponent } = useToast();
  
  // Load service agreements on mount
  useEffect(() => {
    fetchServiceAgreements();
  }, [fetchServiceAgreements]);
  
  // Filter agreements based on user role
  const filteredAgreements = serviceAgreements.filter((agreement: any) => {
    if (userRole === 'Administrador') return true;
    return agreement.generated_by === user?.id;
  });

  // Handle saving a new service agreement
  const handleSaveServiceAgreement = async (agreementData: any) => {
    console.log('[SERVICE_AGREEMENTS] handleSaveServiceAgreement called with:', agreementData);

    try {
      // Prepare agreement data for database
      const agreement = {
        ...agreementData,
        generated_by: user?.id,
        generated_by_name: user?.email?.split('@')[0] || 'Usuario',
        generated_at: new Date().toISOString(),
      };

      // Save to database
      await addServiceAgreement(agreement);

      // Generate PDF
      console.log('[SERVICE_AGREEMENTS] Generating PDF for agreement:', agreement);
      await generateServiceAgreementPDF(agreement);

      toast.success(`Acuerdo de servicio creado exitosamente para ${agreement.variables.client_name}`, 'El PDF se ha generado y descargado.');
      setIsModalOpen(false);
    } catch (error) {
      console.error('[SERVICE_AGREEMENTS] Error saving service agreement:', error);
      toast.error('Error al guardar el acuerdo', 'Inténtalo de nuevo.');
    }
  };

  // Handle downloading PDF
  const handleDownloadPDF = async (agreement: any) => {
    try {
      await generateServiceAgreementPDF(agreement);
      toast.success('PDF generado exitosamente');
    } catch (error) {
      console.error('[SERVICE_AGREEMENTS] Error generating PDF:', error);
      toast.error('Error al generar el PDF', 'Inténtalo de nuevo.');
    }
  };

  // Handle deleting agreement
  const handleDeleteAgreement = async (agreementId: string) => {
    if (window.confirm('¿Estás seguro de que quieres eliminar este acuerdo de servicio?')) {
      try {
        await deleteServiceAgreement(agreementId);
        toast.success('Acuerdo eliminado exitosamente');
      } catch (error) {
        console.error('Error deleting agreement:', error);
        toast.error('Error al eliminar el acuerdo');
      }
    }
  };

  // Handle editing agreement
  const handleEditAgreement = (agreement: any) => {
    setEditingAgreement(agreement);
    setIsModalOpen(true);
  };

  // Handle saving template
  const handleSaveTemplate = (template: any) => {
    console.log('Template saved:', template);
    toast.success('Template guardado exitosamente');
    fetchServiceAgreements();
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-semibold text-gray-900 dark:text-white flex items-center">
          <FileText className="mr-2 h-6 w-6 text-indigo-600 dark:text-indigo-400" />
          Acuerdos de Servicio
        </h2>
        <div className="flex space-x-2">
          {userRole === 'Administrador' && (
            <Button
              variant="outline"
              icon={<Settings className="h-5 w-5" />}
              onClick={() => setIsTemplateEditorOpen(true)}
            >
              Editar Template
            </Button>
          )}
          <Button
            variant="outline"
            icon={<FileText className="h-5 w-5" />}
            onClick={async () => {
              try {
                const sampleAgreement = {
                  template_name: 'Acuerdo de Servicio Productex',
                  contract_date: new Date().toISOString().split('T')[0],
                  variables: {
                    client_name: 'Cliente de Prueba',
                    client_id: '20-12345678-9',
                    client_email: 'cliente@ejemplo.com',
                    production_days: 30
                  }
                };
                await generateServiceAgreementPDF(sampleAgreement);
                toast.success('PDF de prueba generado exitosamente');
              } catch (error) {
                console.error('Error generating test PDF:', error);
                toast.error('Error al generar el PDF de prueba');
              }
            }}
          >
            Probar PDF
          </Button>
          <Button
            variant="primary"
            icon={<Plus className="h-5 w-5" />}
            onClick={() => {
              setEditingAgreement(null);
              setIsModalOpen(true);
            }}
          >
            Nuevo Acuerdo
          </Button>
        </div>
      </div>
      
      {/* Service Agreements List */}
      <Card>
        <CardHeader
          title={`Acuerdos de Servicio (${filteredAgreements.length})`}
        />
        <CardContent>
          {filteredAgreements.length === 0 ? (
            <div className="text-center py-8 text-gray-500 dark:text-gray-400">
              <FileText className="h-12 w-12 mx-auto mb-4 text-gray-400" />
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                No hay acuerdos de servicio registrados
              </h3>
              <p className="mb-4">
                Los acuerdos de servicio te permiten generar contratos con variables configurables.
              </p>
              <Button
                variant="primary"
                icon={<Plus className="h-4 w-4" />}
                onClick={() => {
                  setEditingAgreement(null);
                  setIsModalOpen(true);
                }}
              >
                Crear Primer Acuerdo
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              {filteredAgreements.map((agreement: any) => (
                <div
                  key={agreement.id}
                  className="flex items-center justify-between p-4 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                >
                  <div className="flex-1">
                    <div className="flex items-center space-x-3">
                      <FileText className="h-5 w-5 text-indigo-600 dark:text-indigo-400" />
                      <div>
                        <h3 className="font-medium text-gray-900 dark:text-white">
                          {agreement.variables.client_name || 'Cliente sin nombre'}
                        </h3>
                        <p className="text-sm text-gray-500 dark:text-gray-400">
                          {agreement.template_name} • {new Date(agreement.contract_date).toLocaleDateString('es-AR')}
                        </p>
                        <p className="text-xs text-gray-400 dark:text-gray-500">
                          <span className="inline-flex items-center gap-2">
                            <span>Generado por:</span>
                            <span className="inline-flex items-center rounded-full bg-indigo-50 px-2 py-0.5 text-[11px] font-medium text-indigo-700 ring-1 ring-inset ring-indigo-200 dark:bg-indigo-900/30 dark:text-indigo-200 dark:ring-indigo-800">
                              {agreement.generated_by_name || 'Usuario'}
                            </span>
                          </span>
                          <span> • </span>
                          {new Date(agreement.generated_at).toLocaleDateString('es-AR')} {new Date(agreement.generated_at).toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit' })}
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Button
                      variant="outline"
                      size="sm"
                      icon={<Download className="h-4 w-4" />}
                      onClick={() => handleDownloadPDF(agreement)}
                    >
                      Descargar
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      icon={<Edit className="h-4 w-4" />}
                      onClick={() => handleEditAgreement(agreement)}
                    >
                      Editar
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      icon={<Trash2 className="h-4 w-4" />}
                      onClick={() => handleDeleteAgreement(agreement.id)}
                      className="text-red-600 hover:text-red-700 hover:bg-red-50 dark:text-red-400 dark:hover:text-red-300 dark:hover:bg-red-900/20"
                    >
                      Eliminar
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
      
      {/* Service Agreement Modal */}
      <ServiceAgreementModal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setEditingAgreement(null);
        }}
        onSave={handleSaveServiceAgreement}
        clients={clients}
        editingAgreement={editingAgreement}
      />

      {/* Template Editor Modal */}
      <TemplateEditorModal
        isOpen={isTemplateEditorOpen}
        onClose={() => setIsTemplateEditorOpen(false)}
        onSave={handleSaveTemplate}
      />

      {/* Toast Notifications */}
      {ToastComponent}
    </div>
  );
};

export default ServiceAgreements;
